import React, { useEffect } from 'react';

import { Box } from '@mui/material';
import { m, useAnimationControls } from 'framer-motion';
import { format } from 'date-fns';
import styled from '@emotion/styled';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import {
  getHeatMapItemBgColor,
  getHeatMapItemBorderColor,
  getAnimationDelay,
  GRID_ITEM_ANIMATION_DURATION,
  GRID_ITEM_ICON_ANIMATION_DELAY,
} from '../utils';
import { MasteryHeatMapTooltip } from './MasteryHeatMapTooltip';

const Container = styled(m.div)`
  position: relative;
  display: flex;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  color: #fff;
  cursor: pointer;
  background-color: #e0e2e4;
  border-width: 1px;
  border-style: solid;
  overflow: hidden;
  &:hover {
    border-color: #ea0bbd !important;
    background-color: #ea0bbd !important;
  }
`;

const IconContainer = styled(m.div)`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

type Props = {
  mastered: number | null | undefined;
  date: string;
  isIntersecting: boolean;
};

export const MasteryHeatMapGridItem = ({ mastered, date, isIntersecting }: Props) => {
  const controls = useAnimationControls();
  const iconControls = useAnimationControls();
  const isToday = date === format(new Date(), 'yyyy-MM-dd');
  const bgColor = getHeatMapItemBgColor(mastered ?? 0);
  const borderColor = getHeatMapItemBorderColor(mastered ?? 0, isToday, bgColor);
  const showCheckMark = mastered && mastered >= 2;

  useEffect(() => {
    if (!isIntersecting) {
      return;
    }

    // fadeIn grid container
    controls.start({
      opacity: 1,
      backgroundColor: ['#E0E2E4', bgColor],
      borderColor: ['#E0E2E4', borderColor],
      transition: {
        duration: GRID_ITEM_ANIMATION_DURATION,
        delay: getAnimationDelay(mastered),
      },
    });

    // Animate icon once container finishes animation
    iconControls.start({
      scale: 1,
      transition: { duration: 1, delay: GRID_ITEM_ICON_ANIMATION_DELAY },
    });
  }, [isIntersecting, bgColor, borderColor, mastered]);

  return (
    <MasteryHeatMapTooltip mastered={mastered} date={date}>
      <Box position="relative">
        <Container animate={controls}>
          {!!showCheckMark && (
            <IconContainer animate={iconControls} initial={{ scale: 0 }}>
              <CheckRoundedIcon sx={{ fontSize: '18px' }} />
            </IconContainer>
          )}
        </Container>
      </Box>
    </MasteryHeatMapTooltip>
  );
};
